import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiArrowLeft, FiPlay, FiFile, FiClipboard, FiLink,
  FiBookOpen, FiCheckCircle, FiCircle, FiExternalLink,
  FiClock, FiAward, FiChevronDown, FiChevronRight,
  FiUpload, FiStar, FiInfo
} from 'react-icons/fi';
import { getMyEnrollment, markItemComplete, submitAssignment } from '../utils/api';

/* ── helpers ── */
const typeIcon = (type, size = 16) => {
  const map = {
    video: <FiPlay size={size} color="#1d4ed8" />,
    file: <FiFile size={size} color="#7c3aed" />,
    assignment: <FiClipboard size={size} color="#b45309" />,
    note: <FiBookOpen size={size} color="#dc2626" />,
    link: <FiLink size={size} color="#059669" />,
  };
  return map[type] || <FiFile size={size} />;
};

const typeLabel = { video: 'Video', file: 'File', assignment: 'Assignment', note: 'Note', link: 'Link' };

const typeColor = {
  video: { bg: '#dbeafe', color: '#1d4ed8' },
  file: { bg: '#f3e8ff', color: '#7c3aed' },
  assignment: { bg: '#fef3c7', color: '#b45309' },
  note: { bg: '#fee2e2', color: '#dc2626' },
  link: { bg: '#d1fae5', color: '#059669' },
};

const getEmbedUrl = (url = '') => {
  if (!url) return null;
  // YouTube
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([A-Za-z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0&modestbranding=1`;
  // Google Drive
  const gdMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (gdMatch) return `https://drive.google.com/file/d/${gdMatch[1]}/preview`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  // Direct embed URL
  if (url.includes('embed') || url.includes('iframe')) return url;
  return null;
};

/* ── Progress Ring ── */
const ProgressRing = ({ pct = 0, size = 64 }) => {
  const r = (size - 8) / 2, circ = 2 * Math.PI * r, dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={6} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--primary)" strokeWidth={6}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transformOrigin: '50% 50%', transform: 'rotate(-90deg)', transition: 'stroke-dasharray 0.6s ease' }} />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: 13, fontWeight: 700, fill: 'var(--dark)', fontFamily: 'var(--font-display)' }}>{pct}%</text>
    </svg>
  );
};

/* ── Content Viewer ── */
const ContentViewer = ({ item, enrollment, onComplete, onSubmitAssignment }) => {
  const [submitUrl, setSubmitUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const embedUrl = item.type === 'video' ? getEmbedUrl(item.videoUrl) : null;
  const isDone = enrollment?.completedItems?.some(id => id === item._id || id?.toString() === item._id?.toString());

  const handleSubmit = async () => {
    if (!submitUrl.trim()) { toast.error('Please enter your submission URL or link.'); return; }
    setSubmitting(true);
    try {
      await onSubmitAssignment(item._id, submitUrl);
      toast.success('Assignment submitted! 🎉');
      setSubmitUrl('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  const tc = typeColor[item.type] || typeColor.file;

  return (
    <div className="cv-wrap">
      <style>{`
        .cv-wrap { animation: fadeInUp 0.3s ease; }
        .cv-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 20px; }
        .cv-type-badge { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; font-family: var(--font-display); }
        .cv-title { font-size: 20px; font-weight: 700; margin-bottom: 6px; line-height: 1.3; }
        .cv-desc { color: var(--mid); font-size: 14px; line-height: 1.7; margin-bottom: 20px; }
        .video-frame { width: 100%; aspect-ratio: 16/9; border-radius: var(--radius-lg); overflow: hidden; background: #000; margin-bottom: 20px; }
        .video-frame iframe { width: 100%; height: 100%; border: none; }
        .file-download-box { background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
        .file-icon { width: 52px; height: 52px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 26px; flex-shrink: 0; }
        .note-box { background: #fffbeb; border: 1px solid #fde68a; border-left: 4px solid #f59e0b; border-radius: var(--radius); padding: 18px 20px; margin-bottom: 20px; white-space: pre-wrap; font-size: 14px; line-height: 1.8; color: var(--dark-3); }
        .assignment-box { background: var(--accent-light); border: 1px solid #fed7aa; border-radius: var(--radius-lg); padding: 20px; margin-bottom: 20px; }
        .assignment-meta { display: flex; gap: 20px; margin-bottom: 14px; flex-wrap: wrap; }
        .assignment-meta-item { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--dark-3); }
        .submit-area { background: white; border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; margin-top: 16px; }
        .submit-area h4 { font-size: 15px; margin-bottom: 4px; }
        .submit-area p { font-size: 13px; color: var(--mid); margin-bottom: 14px; }
        .submit-status { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-radius: var(--radius); font-size: 14px; font-weight: 500; margin-top: 14px; }
        .grade-box { background: #d1fae5; border: 1px solid #6ee7b7; border-radius: var(--radius-lg); padding: 16px 20px; margin-top: 16px; }
        .link-box { background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; margin-bottom: 20px; display: flex; align-items: center; gap: 16px; }
        .mark-complete-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 14px; border-radius: var(--radius-lg); font-size: 15px; font-weight: 600; font-family: var(--font-display); cursor: pointer; border: 2px solid; transition: var(--transition); margin-top: 8px; }
        .mark-complete-btn.done { background: #d1fae5; color: var(--success); border-color: #6ee7b7; }
        .mark-complete-btn.not-done { background: var(--primary); color: white; border-color: var(--primary); }
        .mark-complete-btn.not-done:hover { background: var(--primary-dark); transform: translateY(-2px); }
      `}</style>

      <div className="cv-header">
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <span className="cv-type-badge" style={{ background: tc.bg, color: tc.color }}>
              {typeIcon(item.type, 13)} {typeLabel[item.type]}
            </span>
            {item.videoDuration && <span style={{ fontSize: 12, color: 'var(--mid)', display: 'flex', alignItems: 'center', gap: 4 }}><FiClock size={12} /> {item.videoDuration}</span>}
            {item.maxMarks && <span style={{ fontSize: 12, color: 'var(--mid)', display: 'flex', alignItems: 'center', gap: 4 }}><FiStar size={12} /> {item.maxMarks} marks</span>}
          </div>
          <h2 className="cv-title">{item.title}</h2>
          {item.description && <p className="cv-desc">{item.description}</p>}
        </div>
      </div>

      {/* VIDEO */}
      {item.type === 'video' && (
        embedUrl ? (
          <div className="video-frame"><iframe src={embedUrl} title={item.title} allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" /></div>
        ) : item.videoUrl ? (
          <div className="link-box">
            <div style={{ width: 48, height: 48, borderRadius: 12, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>▶️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Video Lecture</div>
              <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 8 }}>Click the button to open the video in a new tab.</div>
              <a href={item.videoUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm"><FiExternalLink size={13} /> Open Video</a>
            </div>
          </div>
        ) : <div className="cv-desc">No video URL provided.</div>
      )}

      {/* FILE */}
      {item.type === 'file' && (
        <div className="file-download-box">
          <div className="file-icon" style={{ background: '#f3e8ff' }}>📄</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.fileName || item.title}</div>
            <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 8 }}>
              {item.fileType && <span style={{ marginRight: 10 }}>Type: {item.fileType.toUpperCase()}</span>}
              {item.fileSize && <span>Size: {item.fileSize}</span>}
            </div>
            {item.fileUrl && <a href={item.fileUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm"><FiExternalLink size={13} /> Open / Download File</a>}
          </div>
        </div>
      )}

      {/* NOTE */}
      {item.type === 'note' && (
        <div className="note-box">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontWeight: 700, color: '#92400e' }}>
            <FiInfo size={16} /> Instructor Note
          </div>
          {item.description || 'No additional content provided.'}
        </div>
      )}

      {/* LINK */}
      {item.type === 'link' && (
        <div className="link-box">
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>🔗</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>External Resource</div>
            <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 8, wordBreak: 'break-all' }}>{item.linkUrl}</div>
            {item.linkUrl && <a href={item.linkUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm"><FiExternalLink size={13} /> Open Link</a>}
          </div>
        </div>
      )}

      {/* ASSIGNMENT */}
      {item.type === 'assignment' && (
        <div className="assignment-box">
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, color: '#92400e' }}>📝 Assignment Details</div>
          <div className="assignment-meta">
            {item.dueDate && <div className="assignment-meta-item"><FiClock size={13} /> Due: {new Date(item.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>}
            {item.maxMarks && <div className="assignment-meta-item"><FiStar size={13} /> Max Marks: {item.maxMarks}</div>}
          </div>
          {item.description && <p style={{ fontSize: 14, color: 'var(--dark-3)', lineHeight: 1.7 }}>{item.description}</p>}

          {/* Graded result */}
          {item.marksObtained !== undefined && item.marksObtained !== null && (
            <div className="grade-box">
              <div style={{ fontWeight: 700, color: 'var(--success)', marginBottom: 4 }}>✅ Assignment Graded!</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Score: {item.marksObtained} / {item.maxMarks}</div>
              {item.feedback && <div style={{ fontSize: 13, color: 'var(--dark-3)', marginTop: 6 }}>Feedback: {item.feedback}</div>}
            </div>
          )}

          {/* Submission area */}
          <div className="submit-area">
            <h4>Submit Your Work</h4>
            <p>Paste a link to your submission (Google Drive, GitHub, Google Doc, etc.)</p>
            {item.submittedAt ? (
              <div>
                <div className="submit-status" style={{ background: '#d1fae5', color: 'var(--success)' }}>
                  <FiCheckCircle size={15} /> Submitted on {new Date(item.submittedAt).toLocaleDateString('en-IN')}
                </div>
                {item.submissionUrl && <a href={item.submissionUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--primary)', fontSize: 13, marginTop: 8, fontWeight: 600 }}><FiExternalLink size={13} /> View Submission</a>}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input value={submitUrl} onChange={e => setSubmitUrl(e.target.value)} className="form-control" placeholder="https://drive.google.com/..." style={{ flex: 1, minWidth: 200 }} />
                <button onClick={handleSubmit} disabled={submitting} className="btn btn-accent">
                  {submitting ? 'Submitting…' : <><FiUpload size={14} /> Submit</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mark complete button */}
      <button
        onClick={() => !isDone && onComplete(item._id)}
        className={`mark-complete-btn ${isDone ? 'done' : 'not-done'}`}
      >
        {isDone ? <><FiCheckCircle size={18} /> Completed!</> : <><FiCircle size={18} /> Mark as Complete</>}
      </button>
    </div>
  );
};

/* ── Main Page ── */
export default function CourseLearningPage() {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [openModules, setOpenModules] = useState({});
  const [completing, setCompleting] = useState(false);

  const load = useCallback(() => {
    getMyEnrollment(enrollmentId)
      .then(r => {
        setEnrollment(r.data.enrollment);
        // Auto-open first module, auto-select first visible item
        if (r.data.enrollment?.modules?.length > 0) {
          const firstMod = r.data.enrollment.modules[0];
          setOpenModules({ [firstMod._id]: true });
          const firstItem = firstMod.items?.find(i => i.isVisible);
          if (firstItem) setActiveItem(prev => prev || firstItem);
        }
      })
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
  }, [enrollmentId, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (itemId) => {
    if (completing) return;
    setCompleting(true);
    try {
      const res = await markItemComplete(enrollmentId, itemId);
      setEnrollment(prev => ({
        ...prev,
        completedItems: [...(prev.completedItems || []), itemId],
        progressPercent: res.data.progressPercent
      }));
      toast.success('Marked as complete! 🎉');
    } catch { toast.error('Could not update progress.'); }
    finally { setCompleting(false); }
  };

  const handleSubmitAssignment = async (itemId, url) => {
    await submitAssignment(enrollmentId, itemId, { submissionUrl: url });
    load(); // reload to get updated submission state
  };

  const toggleModule = (id) => setOpenModules(o => ({ ...o, [id]: !o[id] }));

  const allItems = enrollment?.modules?.flatMap(m => m.items?.filter(i => i.isVisible) || []) || [];
  const totalItems = allItems.length;
  const doneCount = enrollment?.completedItems?.length || 0;

  if (loading) return (
    <div style={{ paddingTop: 70, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (!enrollment) return null;
  const course = enrollment.course || {};

  return (
    <div style={{ paddingTop: 70, background: 'var(--bg)', minHeight: '100vh' }}>
      <style>{`
        .lms-layout { display: grid; grid-template-columns: 300px 1fr; min-height: calc(100vh - 70px); }
        .lms-sidebar { background: white; border-right: 1px solid var(--border); display: flex; flex-direction: column; position: sticky; top: 70px; height: calc(100vh - 70px); overflow-y: auto; }
        .lms-sidebar-header { padding: 18px 16px; border-bottom: 1px solid var(--border); }
        .lms-sidebar-header h3 { font-size: 14px; font-weight: 700; line-height: 1.3; margin-bottom: 10px; color: var(--dark); }
        .lms-progress-bar { height: 6px; background: var(--border); border-radius: 99px; overflow: hidden; margin-bottom: 6px; }
        .lms-progress-fill { height: 100%; background: linear-gradient(90deg, var(--primary), var(--accent)); border-radius: 99px; transition: width 0.5s ease; }
        .lms-progress-text { display: flex; justify-content: space-between; font-size: 11px; color: var(--mid); }
        .lms-module { border-bottom: 1px solid var(--border); }
        .lms-module-header { display: flex; align-items: center; gap: 10px; padding: 12px 14px; cursor: pointer; background: none; border: none; width: 100%; text-align: left; transition: var(--transition); }
        .lms-module-header:hover { background: var(--bg); }
        .lms-module-title { font-size: 13px; font-weight: 600; color: var(--dark); flex: 1; line-height: 1.35; }
        .lms-module-count { font-size: 11px; color: var(--mid); flex-shrink: 0; }
        .lms-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px 10px 24px; cursor: pointer; transition: var(--transition); border: none; background: none; width: 100%; text-align: left; }
        .lms-item:hover { background: var(--bg); }
        .lms-item.active { background: var(--primary-light); border-left: 3px solid var(--primary); padding-left: 21px; }
        .lms-item-title { font-size: 13px; color: var(--dark-3); flex: 1; line-height: 1.35; }
        .lms-item.active .lms-item-title { color: var(--primary); font-weight: 600; }
        .lms-item-check { color: var(--success); flex-shrink: 0; }
        .lms-main { padding: 32px; max-width: 900px; }
        .lms-topbar { display: flex; align-items: center; gap: 12px; margin-bottom: 28px; flex-wrap: wrap; }
        .lms-nav-btns { display: flex; gap: 8px; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--border); }
        .lms-welcome { background: white; border: 1px solid var(--border); border-radius: var(--radius-xl); padding: 40px; text-align: center; }
        @media (max-width: 900px) {
          .lms-layout { grid-template-columns: 1fr; }
          .lms-sidebar { position: static; height: auto; border-right: none; border-bottom: 1px solid var(--border); max-height: 320px; overflow-y: auto; }
          .lms-main { padding: 20px 16px; }
        }
      `}</style>

      <div className="lms-layout">
        {/* SIDEBAR */}
        <div className="lms-sidebar">
          <div className="lms-sidebar-header">
            <Link to="/dashboard?tab=lms" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--primary)', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              <FiArrowLeft size={14} /> My Courses
            </Link>
            <h3>{course.title}</h3>
            <div className="lms-progress-bar">
              <div className="lms-progress-fill" style={{ width: `${enrollment.progressPercent || 0}%` }} />
            </div>
            <div className="lms-progress-text">
              <span>{doneCount}/{totalItems} completed</span>
              <span>{enrollment.progressPercent || 0}%</span>
            </div>
          </div>

          {/* Module list */}
          {(enrollment.modules || []).sort((a, b) => a.order - b.order).map(mod => {
            const visItems = (mod.items || []).filter(i => i.isVisible).sort((a, b) => a.order - b.order);
            const modDone = visItems.filter(i => enrollment.completedItems?.some(id => id === i._id || id?.toString() === i._id?.toString())).length;
            const isOpen = openModules[mod._id];
            return (
              <div key={mod._id} className="lms-module">
                <button className="lms-module-header" onClick={() => toggleModule(mod._id)}>
                  {isOpen ? <FiChevronDown size={15} color="var(--mid)" /> : <FiChevronRight size={15} color="var(--mid)" />}
                  <span className="lms-module-title">{mod.title}</span>
                  <span className="lms-module-count">{modDone}/{visItems.length}</span>
                </button>
                {isOpen && visItems.map(item => {
                  const done = enrollment.completedItems?.some(id => id === item._id || id?.toString() === item._id?.toString());
                  const isActive = activeItem?._id === item._id;
                  return (
                    <button key={item._id} className={`lms-item${isActive ? ' active' : ''}`} onClick={() => setActiveItem(item)}>
                      <span style={{ flexShrink: 0, opacity: 0.7 }}>{typeIcon(item.type, 13)}</span>
                      <span className="lms-item-title">{item.title}</span>
                      {done && <FiCheckCircle size={13} className="lms-item-check" />}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Batch info */}
          {(enrollment.batchName || enrollment.batchStartDate) && (
            <div style={{ padding: 14, margin: '8px 12px', background: 'var(--primary-light)', borderRadius: 'var(--radius)', fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: 4 }}>Batch Info</div>
              {enrollment.batchName && <div style={{ color: 'var(--dark-3)' }}>📅 {enrollment.batchName}</div>}
              {enrollment.batchStartDate && <div style={{ color: 'var(--mid)' }}>Start: {new Date(enrollment.batchStartDate).toLocaleDateString('en-IN')}</div>}
              {enrollment.batchEndDate && <div style={{ color: 'var(--mid)' }}>End: {new Date(enrollment.batchEndDate).toLocaleDateString('en-IN')}</div>}
            </div>
          )}

          {/* Instructor note */}
          {enrollment.studentNote && (
            <div style={{ padding: 14, margin: '8px 12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius)', fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>📌 Note from Instructor</div>
              <div style={{ color: 'var(--dark-3)', lineHeight: 1.6 }}>{enrollment.studentNote}</div>
            </div>
          )}
        </div>

        {/* MAIN CONTENT */}
        <div className="lms-main">
          {/* Top bar */}
          <div className="lms-topbar">
            <ProgressRing pct={enrollment.progressPercent || 0} size={56} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{course.title}</div>
              <div style={{ fontSize: 13, color: 'var(--mid)' }}>{doneCount} of {totalItems} items completed · {course.duration}</div>
            </div>
            {doneCount === totalItems && totalItems > 0 && (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, background: '#d1fae5', color: 'var(--success)', padding: '8px 16px', borderRadius: 'var(--radius)', fontWeight: 700, fontSize: 14 }}>
                <FiAward size={16} /> Course Complete!
              </div>
            )}
          </div>

          {/* Active item content */}
          {activeItem ? (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '28px 32px' }}>
              <ContentViewer
                item={activeItem}
                enrollment={enrollment}
                onComplete={handleComplete}
                onSubmitAssignment={handleSubmitAssignment}
              />

              {/* Prev / Next navigation */}
              <div className="lms-nav-btns">
                {(() => {
                  const prev = allItems[allItems.findIndex(i => i._id === activeItem._id) - 1];
                  const next = allItems[allItems.findIndex(i => i._id === activeItem._id) + 1];
                  return (
                    <>
                      <button onClick={() => prev && setActiveItem(prev)} disabled={!prev} className="btn btn-outline" style={{ opacity: prev ? 1 : 0.3 }}>
                        ← Previous
                      </button>
                      <div style={{ flex: 1 }} />
                      <button onClick={() => next && setActiveItem(next)} disabled={!next} className="btn btn-primary" style={{ opacity: next ? 1 : 0.5 }}>
                        Next →
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="lms-welcome">
              <div style={{ fontSize: 64, marginBottom: 16 }}>
                {course.category === 'Development' ? '💻' : course.category === 'Data Science' ? '🤖' : '📱'}
              </div>
              <h2 style={{ marginBottom: 10 }}>Welcome to {course.title}</h2>
              <p style={{ color: 'var(--mid)', marginBottom: 8, maxWidth: 480, margin: '0 auto 16px' }}>
                {totalItems > 0
                  ? `You have ${totalItems} items to complete across ${enrollment.modules?.length} modules. Select a lesson from the sidebar to begin.`
                  : 'Your course content is being prepared by your instructor. Check back soon!'}
              </p>
              {totalItems > 0 && (
                <button className="btn btn-primary btn-lg" onClick={() => setActiveItem(allItems[0])}>
                  <FiPlay size={16} /> Start Learning
                </button>
              )}
              {enrollment.studentNote && (
                <div style={{ marginTop: 24, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius)', padding: '16px', textAlign: 'left', maxWidth: 480, margin: '24px auto 0' }}>
                  <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>📌 Note from your Instructor</div>
                  <p style={{ fontSize: 14, color: 'var(--dark-3)', lineHeight: 1.7, margin: 0 }}>{enrollment.studentNote}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}